// File contains a collection of methods that get shared between Product
// related components
import { NumberFormatter } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import imageShapeOptions from '../../../../src/constants/imageShapeOptions';
import { LAYOUT1 } from '../../../constants/pdpLayouts';
import dataAids from '../../../constants/dataAids';

export const VERTICAL_RATIO = 0.75;
export const HORIZONTAL_CLASSIC_RATIO = 1.33;
export const HORIZONTAL_HERO_RATIO = 1.5;
export const SQUARE_RATIO = 1;

const getType = (currency, percentage) => {
  if (currency) {
    return 'Currency';
  } else if (percentage) {
    return 'Percentage';
  }
  return 'Number';
};

export const formatPrice = ({ currency, precision, number, percentage }) => {
  const precisionMatters = currency || Number(precision) !== 0;

  const methodName = `toFormatted${getType(currency, percentage)}`;
  return NumberFormatter[methodName](number, precisionMatters, precisionMatters);
};

export const getPersonalizationLabelWithPrice = ({ name, price, translate = () => {} }) => {
  price = +price;
  if (!price) {
    return name;
  }

  const symbol = price > 0 ? '+' : '';
  const formattedPrice = `${symbol}${NumberFormatter.toFormattedCurrency(price)}`;

  return translate('products.option_name_price_increase_v2', {
    option_name: name,
    price_increase: formattedPrice
  });
};

export const priceIsObject = price => {
  return price !== null && typeof price === 'object';
};

const hasSalePriceOld = product => {
  return (
    product['on_sale?'] && product.sale_price !== null && typeof product.sale_price !== 'undefined'
  );
};

export const hasSalePrice = product => {
  if (!priceIsObject(product.sale_price)) {
    return hasSalePriceOld(product);
  }

  return (
    product['on_sale?'] &&
    product.sale_price.numeric !== null &&
    typeof product.sale_price.numeric !== 'undefined'
  );
};

const percentDifference = (normal, discount) => {
  let percentage = Math.round(100 * (1 - discount / normal));
  if (percentage === 100 && parseFloat(discount) !== 0) percentage = 99;

  return percentage;
};

const calculateSalePercentageForPriceObjects = (price, salePrice) => {
  if (price.numeric === null || salePrice.numeric === null) return 0;
  if (price.numeric - salePrice.numeric === 0) return 0;

  return percentDifference(price.numeric, salePrice.numeric);
};

export const calculateSalePercentage = (price, salePrice) => {
  if (price === null || salePrice === null) return 0;
  if (priceIsObject(price)) {
    return calculateSalePercentageForPriceObjects(price, salePrice);
  }
  if (price - salePrice === 0) return 0;

  return percentDifference(price, salePrice);
};

export const determineDesktopFontSize = productsPerRow => {
  switch (productsPerRow) {
    case 5:
      return 'xsmall';
    case 6:
      return 'xsmall';
    default:
      return 'medium';
  }
};

export const determineProductNameFont = productsPerRow => {
  switch (productsPerRow) {
    case 5:
      return 'small';
    case 6:
      return 'small';
    default:
      return 'medium';
  }
};

export const determineStyles = (productsPerRow, options = {}) => {
  const { saleBannerPaddingVertical, isProductName, isOptionOrFreeShipping } = options;

  if (saleBannerPaddingVertical) {
    return productsPerRow === 3 ? 'xsmall' : 'xxsmall';
  }

  if (isOptionOrFreeShipping) return 'xsmall';

  const fontSize = isProductName
    ? determineProductNameFont(productsPerRow)
    : determineDesktopFontSize(productsPerRow);
  return {
    fontSize
  };
};

export const isZoomEnabled = asset => {
  const {
    attachment_width: attachmentWidth,
    attachment_height: attachmentHeight,
    zoom_image_url: zoomImageUrl
  } = asset;

  return zoomImageUrl && Math.max(attachmentWidth, attachmentHeight) > 600;
};

export const getImageRatio = (shape, layout) => {
  if (shape === imageShapeOptions.VERTICAL) {
    return VERTICAL_RATIO;
  }

  if (shape === imageShapeOptions.HORIZONTAL) {
    return layout === LAYOUT1 ? HORIZONTAL_CLASSIC_RATIO : HORIZONTAL_HERO_RATIO;
  }

  return SQUARE_RATIO;
};

export const getProductLabel = (product, translate) => {
  let { labelText, labelDataAid } = {};

  if (product.ship_free) {
    labelText = translate('products.free_shipping');
    labelDataAid = dataAids.PRODUCT_SHIP_FREE_RENDERED;
  }
  if (product.free_return) {
    labelText = translate('products.free_returns');
    labelDataAid = dataAids.PRODUCT_FREE_RETURNS_RENDERED;
  }

  if (product.free_return && product.ship_free) {
    labelText = translate('products.free_shipping_and_returns');
    labelDataAid = dataAids.PRODUCT_FREE_SHIPPING_AND_RETURNS_RENDERED;
  }

  return { labelText, labelDataAid };
};
